import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  Sector,
  SectorFilterInputObject,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Search from '@lib/components/Search/Search';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import FeatureLockState from '@lib/components/FeatureLockState';
import Trans from '@lib/components/Trans/Trans';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { PlanState } from '@lib/enums/plan';
import { TableAction } from '@lib/components/Table/types';
import useTabListState from '@lib/components/Table/utils/useTabListState';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useSectorsQueryHook from 'utils/fetch/hooks/useSectorsQueryHook';
import { APP_URLS } from 'constants/urls';
import getSectorsColumns from './utils/getSectorsColumns';

interface SectorsTabProps {
  documentId: string;
}

export default function SectorsTab({ documentId }: SectorsTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listState, params, searchQuery, onSearchChange } = useTabListState();
  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const additionalFilters: SectorFilterInputObject[] = useMemo(
    () => [
      {
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentId },
          filterGroup: 'documentationsId',
        },
      },
    ],
    [documentId],
  );

  const { collection, firstLoading, loading, totalCount } = useSectorsQueryHook(
    {
      additionalFilters,
      paginationParams: params,
      searchQuery,
      skip: isPlanLocked,
    },
  );

  const onViewHandler = useCallback(
    (item: Sector) => {
      navigate(
        APP_URLS.dashboard.sectors.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  const columns = useMemo(() => getSectorsColumns({ t }), [t]);

  const tableActions: TableAction<Sector>[] = useMemo(
    () => [
      {
        id: 'view-action',
        icon: 'visibility',
        onClick: onViewHandler,
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnTooltipProps: () => ({ body: t('view') }),
      },
    ],
    [onViewHandler, t],
  );

  if (isPlanLocked) {
    return (
      <FeatureLockState
        title={t('unlock-document-access-control')}
        text={<Trans i18nKey="unlock-document-access-control-text" />}
        onUpgrade={() =>
          handleOpenPlanModal(PlanState.Locked, [
            CompanyPlanUpgradeFeatureEnum.Documentation,
          ])
        }
      />
    );
  }

  if (firstLoading) {
    return <CircleLoader />;
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-sectors')}
      text={t('no-linked-sectors-text')}
    />
  );

  return (
    <>
      {((!!totalCount && !!collection.length) || searchQuery) && (
        <Search
          placeholder={t('sectors-search-placeholder')}
          value={searchQuery}
          onChange={onSearchChange}
          useSearchParam={false}
        />
      )}
      <Table
        actions={tableActions}
        columns={columns}
        counterText={t('sector-plural', { count: totalCount })}
        data={collection}
        emptyState={emptyState}
        id="documentSectors"
        loading={loading}
        orderingState={listState}
        paginationState={listState}
        totalEntries={totalCount}
      />
    </>
  );
}
